/**
 * Copyright 2026 Ziz Academy
 */

import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { examApi, submissionApi } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { Exam, ExamSubmission } from '../types';

const StudentDashboard: React.FC = () => {
    const [exams, setExams] = useState<Exam[]>([]);
    const [submissions, setSubmissions] = useState<ExamSubmission[]>([]);
    const [loading, setLoading] = useState(true);
    const { user } = useAuth();

    useEffect(() => {
        const fetchData = async () => {
            try {
                const [examsData, submissionData, assignments] = await Promise.all([
                    user?.id ? examApi.getAssignedExams(user.id) : Promise.resolve([]),
                    user?.id ? submissionApi.getByStudent(user.id) : Promise.resolve([]),
                    user?.id ? examApi.getAssignmentsByStudent(user.id) : Promise.resolve([]),
                ]);
                setExams(examsData);
                setSubmissions(submissionData);
                // Build a map of examId -> max assigned attempt
                const maxAttemptByExam: Record<number, number> = {};
                (assignments as any[]).forEach((a: any) => {
                    const exId = a.examId as number;
                    const attempt = a.attemptNumber as number;
                    if (exId != null && attempt != null) {
                        maxAttemptByExam[exId] = Math.max(maxAttemptByExam[exId] || 0, attempt);
                    }
                });
                // Attach computed field to exams list for easier rendering
                setExams(examsData.map(e => ({ ...e, assignedMaxAttempt: maxAttemptByExam[e.id!] || 1 })) as any);
            } catch (error) {
                console.error('Error fetching data:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [user?.id]);

    const countSubmissionsForExam = (examId: number) => submissions.filter(s => s.examId === examId).length;

    const nextAttemptForExam = (examId: number) => {
        const count = countSubmissionsForExam(examId);
        return Math.min(count + 1, 5);
    };

    // Has the student submitted this exam at least once?
    const hasSubmitted = (examId: number) => submissions.some(s => s.examId === examId);

    const ordinal = (n: number) => {
        const s = ["th", "st", "nd", "rd"], v = n % 100;
        return n + (s[(v - 20) % 10] || s[v] || s[0]);
    };

    const getExamEmoji = (index: number) => {
        const emojis = ['📚', '🧮', '🔬', '🎨', '🌍', '📖', '🎵', '⚽'];
        return emojis[index % emojis.length];
    };

    if (loading) {
        return (
            <div className="loading" data-testid="loading">
                <div className="spinner"></div>
            </div>
        );
    }

    return (
        <div data-testid="student-dashboard">
            <div className="card" style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white' }}>
                <h1 style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>
                    👋 Hello, {user?.name}!
                </h1>
                <p style={{ fontSize: '1.25rem', opacity: 0.9 }}>
                    Ready to learn something amazing today? Let's check out your exams! 🌟
                </p>
            </div>

            <h2 style={{ marginBottom: '1.5rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                📋 Available Exams
            </h2>

            {exams.length === 0 ? (
                <div className="empty-state">
                    <div className="empty-state-emoji">📭</div>
                    <h3 className="empty-state-title">No Exams Assigned</h3>
                    <p>Your teacher hasn't assigned any exams yet. Check back later!</p>
                </div>
            ) : (
                <div className="grid grid-2">
                    {exams.map((exam, index) => (
                        <div key={exam.id} className="card exam-card">
                            <div className="exam-emoji">{getExamEmoji(index)}</div>
                            <h3 className="card-title">{exam.title}</h3>
                            <p className="card-subtitle">{exam.description}</p>

                            <div className="exam-info">
                                <div className="exam-info-item">
                                    <span className="icon">⏱</span>
                                    <span>{exam.durationMinutes} minutes</span>
                                </div>
                                <div className="exam-info-item">
                                    <span className="icon">📊</span>
                                    <span>{exam.totalMarks} marks</span>
                                </div>
                                <div className="exam-info-item">
                                    <span className="icon">👩‍🏫</span>
                                    <span>{exam.createdByName}</span>
                                </div>
                            </div>

                            {(() => {
                                const assignedMaxAttempt = (exam as any).assignedMaxAttempt || 1;
                                const subsForExam = submissions.filter(s => s.examId === exam.id);
                                // Sort by submittedAt desc to get latest first
                                subsForExam.sort((a, b) => {
                                    const da = a.submittedAt ? new Date(a.submittedAt).getTime() : 0;
                                    const db = b.submittedAt ? new Date(b.submittedAt).getTime() : 0;
                                    return db - da;
                                });
                                const submittedCount = subsForExam.length;
                                if (submittedCount < assignedMaxAttempt) {
                                    const next = submittedCount + 1;
                                    return (
                                        <Link to={`/exam/${exam.id}`} className="btn btn-primary" style={{ marginTop: '1rem' }}>
                                            {submittedCount === 0 ? '🚀 Start Exam' : `🚀 Start ${ordinal(next)} Attempt`}
                                        </Link>
                                    );
                                } else {
                                    const latest = subsForExam[0];
                                    return latest ? (
                                        <Link to={`/result/${latest.id}`} className="btn btn-secondary" style={{ marginTop: '1rem' }}>
                                            👀 View Submission (Read-only)
                                        </Link>
                                    ) : (
                                        <div className="badge badge-success" style={{ marginTop: '1rem' }}>
                                            ✅ Already Completed
                                        </div>
                                    );
                                }
                            })()}
                        </div>
                    ))}
                </div>
            )}

            {submissions.length > 0 && (
                <>
                    <h2 style={{ marginTop: '3rem', marginBottom: '1.5rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        🏆 Recent Results
                    </h2>
                    <div className="grid grid-3">
                        {submissions.slice(0, 3).map((sub) => (
                            <Link
                                key={sub.id}
                                to={`/result/${sub.id}`}
                                className="card"
                                style={{ textDecoration: 'none', color: 'inherit' }}
                            >
                                <h4 style={{ marginBottom: '0.5rem' }}>{sub.examTitle}</h4>
                                <div className="score-display" style={{ padding: '1rem', marginBottom: '0' }}>
                                    <div className="score-number" style={{ fontSize: '2rem' }}>
                                        {sub.totalScore}/{sub.totalMarks}
                                    </div>
                                </div>
                                <div style={{ textAlign: 'center', marginTop: '1rem' }}>
                                    {sub.isEvaluated ? (
                                        <span className="badge badge-success">✅ Evaluated</span>
                                    ) : (
                                        <span className="badge badge-warning">⌛ Pending Evaluation</span>
                                    )}
                                </div>
                            </Link>
                        ))}
                    </div>
                </>
            )}
        </div>
    );
};

export default StudentDashboard;
